import React, { useRef, useEffect, useState, useContext } from "react"
import { graphql } from "gatsby"
import cc from "classcat"
import { LocaleContext } from "contexts"
import { motion, useInView } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import { formatArticles } from "utils/prismic"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import SEO from "components/seo"
import Hero from "components/organisms/webinar/Hero"
import Media from "components/organisms/somebuddy/ListHorizontal"
import BlogPosts from "components/organisms/common/BlogPosts"
import Contact from "components/organisms/somebuddy/Contact"
import { Container } from "components/atoms/materials/Container"
import { SectionItem } from "components/molecules/materials/SectionItem"
import { NavBar } from "components/organisms/materials/NavBar"
import { HowItWorks } from "components/organisms/HowItWorks"
import Divided from "components/organisms/common/Divided"
import List from "components/List"
import H3 from "components/atoms/H3"
import SubscribeForm from "components/SubscribeForm"
import { OutboundLink } from "components/atoms/OutboundLink"
import { fillWithUtm } from "utils/url"
import { Disclosured } from "components/organisms/common/DisclosureList"
import LoginTypeIcon from "images/login-type.svg"
import LoginCodeIcon from "images/login-code.svg"
import KeyIcon from "images/icon-key.svg"
import Help from "components/organisms/Help"
import Button from "components/buttons/Button"
import OnboardingSection from "components/molecules/onboarding"
import { siteCountry } from "../../utils/env"
import countries from "../../../config/i18n"

const HeroButton = () => {
  const { locale } = useContext(LocaleContext)
  const { buttonLoginHelp } = useTranslations()
  return (
    <Button
      bgStyle="light"
      label={buttonLoginHelp}
      link={countries[siteCountry][locale].appLink}
      outbound
    />
  )
}

const Professionals = () => (
  <StaticImage
    objectFit="cover"
    objectPosition="left center"
    src="../../images/pro/DSC05564.jpg"
    alt=""
    style={{ display: "contents", minHeight: "2em" }}
  />
)

const Professionals2 = () => (
  <StaticImage
    objectFit="cover"
    objectPosition="right center"
    src="../../images/hr/AdobeStock_323607493.jpeg"
    alt=""
  />
)

const HowToUse = ({ title }) => {
  const {
    appLink,
    onboardingDeployDescription,
    onboardingDeployStep1Title,
    onboardingDeployStep1Item1,
    onboardingDeployStep1Item2,
    onboardingDeployStep1Item3,
    onboardingDeployStep1Item4,
    onboardingDeployStep1Item5,
    onboardingDeployStep1Item6,
    onboardingDeployStep2Title,
    onboardingDeployStep2Description,
    onboardingDeployStep3Title,
    onboardingDeployStep3Description,
  } = useTranslations()

  const list = [
    {
      name: onboardingDeployStep1Title,
      description: (
        <ol className="list-outside list-decimal space-y-2 text-base ">
          <li>
            <OutboundLink href={fillWithUtm(appLink)} target="_blank">
              <span aria-hidden="true">
                {onboardingDeployStep1Item1} palvelu.someturva.fi
              </span>
            </OutboundLink>
          </li>
          <li>{onboardingDeployStep1Item2}</li>
          <li>{onboardingDeployStep1Item3}</li>
          <li>{onboardingDeployStep1Item4}</li>
          <li>{onboardingDeployStep1Item5}</li>
          <li>{onboardingDeployStep1Item6}</li>
        </ol>
      ),
      icon: KeyIcon,
    },
    {
      name: onboardingDeployStep2Title,
      description: onboardingDeployStep2Description,
      icon: LoginTypeIcon,
    },
    {
      name: onboardingDeployStep3Title,
      description: onboardingDeployStep3Description,
      icon: LoginCodeIcon,
    },
  ]
  return (
    <Divided
      Image={Professionals2}
      theme="green"
      title={title}
      fullHeight={false}
      imageClassName="lg:h-[26rem] xl:h-[24rem]"
    >
      <div>
        <div className="mb-4 space-y-4 text-zinc-800">
          <p className="mb-0 ">{onboardingDeployDescription}</p>
        </div>
        <Disclosured list={list} theme="green" />
      </div>
    </Divided>
  )
}
const GirlPhoneImage = () => (
  <StaticImage
    objectFit="cover"
    objectPosition="center"
    src="../../images/AdobeStock_277750441.jpeg"
    alt=""
    style={{ display: "contents" }}
  />
)

const Situations = () => {
  const {
    onboardingSection2Title,
    onboardingSituationsDescription1,
    onboardingSituationsList1,
    onboardingSituationsList2,
    onboardingSituationsList3,
    onboardingSituationsList4,
    onboardingSituationsList5,
    onboardingSituationsList6,
    onboardingSituationsList7,
    onboardingSituationsList8,
    onboardingSituationsList9,
    onboardingSituationsDescription2,
  } = useTranslations()
  return (
    <Divided
      Image={GirlPhoneImage}
      theme="light"
      reversed
      title={onboardingSection2Title}
    >
      <>
        <p className="mb-0 text-lg">{onboardingSituationsDescription1}</p>
        <div className="relative my-4 lg:order-1">
          <List
            items={[
              { title: onboardingSituationsList1, id: 1 },
              { title: onboardingSituationsList2, id: 2 },
              { title: onboardingSituationsList3, id: 3 },
              { title: onboardingSituationsList4, id: 4 },
              { title: onboardingSituationsList5, id: 5 },
              { title: onboardingSituationsList6, id: 6 },
              { title: onboardingSituationsList7, id: 7 },
              { title: onboardingSituationsList8, id: 8 },
              { title: onboardingSituationsList9, id: 9 },
            ]}
            bullet={<CheckBadgeIcon className="w-6 text-kkm-green" />}
            textStyle="dark"
          />
        </div>
        <p className="mb-0 text-lg">{onboardingSituationsDescription2}</p>
      </>
    </Divided>
  )
}

const HowItWorksTranslations = () => {
  const {
    buttonLoginHelp,
    onboardingSection1Title,
    onboardingFeature1Title,
    onboardingFeature1Description,
    onboardingFeature2Title,
    onboardingFeature2Description,
    onboardingFeature3Title,
    onboardingFeature3Description,
  } = useTranslations()
  return (
    <HowItWorks
      titleAlign="left"
      translations={{
        title: onboardingSection1Title,
        feature1Title: onboardingFeature1Title,
        feature1Text: onboardingFeature1Description,
        feature2Title: onboardingFeature2Title,
        feature2Text: onboardingFeature2Description,
        feature3Title: onboardingFeature3Title,
        feature3Text: onboardingFeature3Description,
        buttonLogin: buttonLoginHelp,
      }}
    />
  )
}

const Section = ({ id, title, description, Content }) => {
  return (
    <motion.section
      layout
      id={id}
      aria-labelledby={title}
      className="scroll-mt-14 sm:scroll-mt-14 lg:scroll-mt-24"
    >
      <Content title={title} description={description} />
    </motion.section>
  )
}

const HeroImage = () => (
  <StaticImage src="../../images/pro/AdobeStock_136592844.jpeg" alt="" />
)

const BlogPostsGreen = () => <BlogPosts theme="green" />

const ProPage = ({ data, pageContext: { currentPage } }) => {
  const {
    pageProOnboardingTitle,
    onboardingHeroTitle,
    onboardingHeroDescription,
    onboardingVideoTItle,
    onboardingIntroTitle,
    onboardingIntroDescription1,
    onboardingIntroDescription2,
    onboardingSection1Title,
    onboardingSection2Title,
    onboardingSection3Title,
    onboardingSection4Title,
    onboardingSection5Title,
    onboardingVideoDescription1,
    onboardingVideoDescription2,
    onboardingVideoDescription3,
    blogTitle,
  } = useTranslations()
  const [showSectionBoxes, setShowSectionBoxes] = useState(false)
  const { locale } = useContext(LocaleContext)

  const prismicArticles =
    data && data.allPrismicNewsArticle
      ? data.allPrismicNewsArticle
      : { edges: [] }
  const articles = formatArticles(prismicArticles)
  const ref = useRef(null)
  const isInView = useInView(ref)
  useEffect(() => {
    if (isInView) setShowSectionBoxes(true)
  }, [isInView])

  const variants = {
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        staggerChildren: 0.5,
        delayChildren: 0.5,
      },
    },
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.3,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  }
  const sections = [
    {
      id: "palvelu",
      title: onboardingSection1Title,
      content: HowItWorksTranslations,
    },
    {
      id: "situations",
      title: onboardingSection2Title,
      content: Situations,
    },
    {
      id: "howto",
      title: onboardingSection3Title,
      content: HowToUse,
    },
    {
      id: "help",
      title: onboardingSection4Title,
      content: () => <Help Image={Professionals} />,
    },
    {
      id: "contact",
      title: onboardingSection5Title,
      content: Contact,
    },
  ]

  if (["fi-fi", "en-fi"].includes(locale))
    sections.push({
      id: "blog",
      title: blogTitle,
      content: BlogPostsGreen,
    })

  return (
    <>
      <SEO title={pageProOnboardingTitle} />
      <div className="sections">
        <Hero
          descriptionBelow
          title={onboardingHeroTitle}
          description={onboardingHeroDescription}
          HeroButton={HeroButton}
          bgColor="green"
          Image={HeroImage}
        />
        <OnboardingSection
          id="introvideo"
          title={onboardingVideoTItle}
          descriptions={[
            onboardingVideoDescription1,
            onboardingVideoDescription2,
            onboardingVideoDescription3,
          ]}
          videoLink="https://player.vimeo.com/video/817178017?h=93741aba35&amp;autoplay=1&amp;loop=0&amp;autopause=0&amp;&amp;badge=0&amp;player_id=0&amp;app_id=58479"
        />
        <div className="mb-10 mt-12 space-y-10 lg:mb-16">
          <Container size="lg" className="flex flex-col space-y-2">
            <H3 textAlign="left" textStyle="dark">
              {onboardingIntroTitle}
            </H3>
            <div className="flex flex-col items-start md:flex-row md:space-x-10">
              <div className="prose md:max-w-md">
                <p>{onboardingIntroDescription1}</p>
                <p>{onboardingIntroDescription2}</p>
              </div>
              <motion.ul
                variants={variants}
                className="grid-col-1 mt-2 hidden w-full gap-x-8 text-slate-900 xs:grid xs:grid-cols-8 lg:w-auto"
                animate={showSectionBoxes ? "open" : "hidden"}
                initial="hidden"
                ref={ref}
                layout
              >
                {Object.values(sections).map((s, index) => (
                  <SectionItem
                    key={s.id}
                    title={s.title}
                    id={s.id}
                    color="accentColorHover"
                    uppercase={false}
                    className={cc([
                      "col-span-4",
                      {
                        "col-end-7":
                          index === sections.length - 1 &&
                          sections.length % 2 !== 0,
                      },
                    ])}
                  />
                ))}
              </motion.ul>
            </div>
          </Container>
        </div>
        <NavBar sections={sections} theme="green" />
        {sections.map((section) => (
          <Section
            id={section.id}
            title={section.title}
            description={false}
            Content={section.content}
          />
        ))}
        <SubscribeForm />
        {locale === "fi-fi" ? (
          <Media items={articles} allItemsLink="/media" theme="dark" />
        ) : null}
      </div>
    </>
  )
}

export default ProPage

export const pageQuery = graphql`
  query ($locale: String!, $currentPage: String) {
    allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      limit: 3
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "DD.MM.YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
  }
`
